import { Component, EventEmitter, Output, inject, signal, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../core/services/api';
import { NotificationService } from '../../../core/services/notification';
import { UiSelectComponent, SelectOption } from '../ui-select/ui-select';
import { DatePickerComponent } from '../date-picker/date-picker';
import { CustomValidators } from '../../validators/custom-validators';

@Component({
    selector: 'app-custom-tour-modal-v2',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, UiSelectComponent, DatePickerComponent],
    encapsulation: ViewEncapsulation.None,
    template: `
    <div class="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" (click)="close()"></div>

      <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative transform rounded-2xl bg-white text-left shadow-xl transition-all w-full mb-4 sm:my-8 sm:w-full sm:max-w-2xl" (click)="$event.stopPropagation()">
          
          <!-- Header -->
          <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 rounded-t-2xl">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-enduro-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.875 1.875 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.159.69.159 1.006 0Z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">
                    {{ 'EVENTS.CUSTOM_TOUR.TITLE' | translate }}
                </h3>
                <button (click)="close()" class="absolute top-4 right-4 text-gray-400 hover:text-enduro-orange transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    {{ 'EVENTS.CUSTOM_TOUR.SUBTITLE' | translate }}
                  </p>
                </div>

                <!-- Form -->
                <div class="mt-6">
                  <form [formGroup]="tourForm" (ngSubmit)="onSubmit()" class="space-y-4">
                    
                    <!-- Section 1 -->
                    <div>
                        <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b border-gray-100">
                            {{ 'EVENTS.CUSTOM_TOUR.CONTACT_INFO' | translate }}
                        </h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Name -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ 'EVENTS.CUSTOM_TOUR.NAME' | translate }} <span class="text-red-600">*</span>
                                </label>
                                <input 
                                    type="text" 
                                    formControlName="name"
                                    [placeholder]="'EVENTS.CUSTOM_TOUR.PLACEHOLDERS.NAME' | translate"
                                    [class.border-red-500]="tourForm.get('name')?.invalid && tourForm.get('name')?.touched"
                                    class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                                >
                                @if (tourForm.get('name')?.invalid && tourForm.get('name')?.touched) {
                                    <p class="text-xs text-red-600 mt-1">
                                        {{ 'EVENTS.CUSTOM_TOUR.ERRORS.NAME_REQUIRED' | translate }}
                                    </p>
                                }
                            </div>

                            <!-- Email -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ 'EVENTS.CUSTOM_TOUR.EMAIL' | translate }} <span class="text-red-600">*</span>
                                </label>
                                <input 
                                    type="email" 
                                    formControlName="email"
                                    [placeholder]="'EVENTS.CUSTOM_TOUR.PLACEHOLDERS.EMAIL' | translate"
                                    [class.border-red-500]="tourForm.get('email')?.invalid && tourForm.get('email')?.touched"
                                    class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                                >
                                @if (tourForm.get('email')?.invalid && tourForm.get('email')?.touched) {
                                    <p class="text-xs text-red-600 mt-1">
                                        {{ 'EVENTS.CUSTOM_TOUR.ERRORS.EMAIL_INVALID' | translate }}
                                    </p>
                                }
                            </div>
                        </div>

                        <div class="mt-4">
                            <!-- Phone -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ 'EVENTS.CUSTOM_TOUR.PHONE' | translate }} <span class="text-red-600">*</span>
                                </label>
                                <input 
                                    type="tel" 
                                    formControlName="phone"
                                    [placeholder]="'EVENTS.CUSTOM_TOUR.PLACEHOLDERS.PHONE' | translate"
                                    [class.border-red-500]="tourForm.get('phone')?.invalid && tourForm.get('phone')?.touched"
                                    class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                                >
                                @if (tourForm.get('phone')?.invalid && tourForm.get('phone')?.touched) {
                                    <p class="text-xs text-red-600 mt-1">
                                        {{ 'EVENTS.CUSTOM_TOUR.ERRORS.INVALID_PHONE' | translate }}
                                    </p>
                                }
                            </div>
                        </div>
                    </div>

                    <!-- Section 2 -->
                    <div class="pt-2">
                        <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b border-gray-100">
                            {{ 'EVENTS.CUSTOM_TOUR.TOUR_DETAILS' | translate }}
                        </h4>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Date -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ 'EVENTS.CUSTOM_TOUR.PREFERRED_DATE' | translate }} <span class="text-red-600">*</span>
                                </label>
                                <app-date-picker 
                                    formControlName="tour_date"
                                    [placeholder]="'EVENTS.DATE_PLACEHOLDER' | translate">
                                </app-date-picker>
                                @if (tourForm.get('tour_date')?.invalid && tourForm.get('tour_date')?.touched) {
                                    <p class="text-xs text-red-600 mt-1">
                                        {{ 'EVENTS.CUSTOM_TOUR.ERRORS.DATE_REQUIRED' | translate }}
                                    </p>
                                }
                            </div>

                            <!-- Participants -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ 'EVENTS.CUSTOM_TOUR.PARTICIPANTS_LBL' | translate }} <span class="text-red-600">*</span>
                                </label>
                                <input 
                                    type="number" 
                                    formControlName="participants"
                                    min="1"
                                    max="50"
                                    [class.border-red-500]="tourForm.get('participants')?.invalid && tourForm.get('participants')?.touched"
                                    class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                                >
                            </div>

                            <!-- Level -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ 'EVENTS.CUSTOM_TOUR.LEVEL_LBL' | translate }} <span class="text-red-600">*</span>
                                </label>
                                <app-ui-select 
                                    formControlName="difficulty"
                                    [options]="difficultyOptions"
                                    [placeholder]="'COMMON.SELECT' | translate">
                                </app-ui-select>
                            </div>
                        </div>

                        <!-- Notes -->
                        <div class="mt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                {{ 'EVENTS.CUSTOM_TOUR.NOTES' | translate }}
                            </label>
                            <textarea 
                                formControlName="notes"
                                rows="3"
                                class="w-full rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            ></textarea>
                        </div>
                    </div>

                    <!-- Messages -->
                    @if (errorMessage()) {
                        <div class="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                             {{ errorMessage() }}
                        </div>
                    }
                    @if (successMessage()) {
                        <div class="mt-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-md">
                             {{ successMessage() }}
                        </div>
                    }
                  </form>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="bg-gray-50 px-4 pb-4 pt-3 sm:px-6 rounded-b-2xl">
            <div class="sm:flex sm:justify-end sm:items-center sm:ml-14">
              <button 
                  type="button" 
                  (click)="onSubmit()"
                  [disabled]="loading() || successMessage() || tourForm.invalid"
                  class="inline-flex w-full justify-center rounded-md bg-enduro-orange px-8 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-opacity-90 sm:w-auto transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                  @if (loading()) { <span class="animate-spin mr-2">⟳</span> }
                  {{ 'EVENTS.CUSTOM_TOUR.SEND' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class CustomTourModalComponent {
    @Output() closed = new EventEmitter<void>();

    private fb = inject(FormBuilder);
    private api = inject(ApiService);
    private notification = inject(NotificationService);
    private translate = inject(TranslateService);

    loading = signal(false);
    errorMessage = signal('');
    successMessage = signal('');

    difficultyOptions: SelectOption[] = [
        { label: 'EVENTS.DIFFICULTY_EASY', value: 'easy' },
        { label: 'EVENTS.DIFFICULTY_MEDIUM', value: 'medium' },
        { label: 'EVENTS.DIFFICULTY_HARD', value: 'hard' }
    ];

    tourForm = this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2), CustomValidators.noNumbers]],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.required, CustomValidators.phone]],
        tour_date: ['', Validators.required],
        participants: [1, [Validators.required, Validators.min(1)]],
        difficulty: ['medium', Validators.required],
        notes: ['']
    });

    close() {
        this.closed.emit();
    }

    onSubmit() {
        Object.keys(this.tourForm.controls).forEach(key => {
            this.tourForm.get(key)?.markAsTouched();
        });

        if (this.tourForm.invalid) {
            this.translate.get('EVENTS.CUSTOM_TOUR.ERRORS.MISSING_FIELDS').subscribe(res => {
                this.errorMessage.set(res || 'Compila tutti i campi obbligatori');
            });
            return;
        }

        if (this.loading()) return;

        this.loading.set(true);
        this.errorMessage.set('');
        this.successMessage.set('');

        this.api.post('custom-tours', this.tourForm.value).subscribe({
            next: () => {
                const name = this.tourForm.value.name;
                const tourDate = this.tourForm.value.tour_date;
                const formattedDate = tourDate ? new Date(tourDate).toLocaleDateString('it-IT') : '';

                this.translate.get('EVENTS.CUSTOM_TOUR.SUCCESS', { name, date: formattedDate }).subscribe(res => {
                    this.successMessage.set(res);
                });

                this.loading.set(false);
                this.tourForm.reset({ participants: 1 });
                setTimeout(() => this.close(), 3000);
            },
            error: (err) => {
                console.error(err);
                // Extract backend error message
                const backendError = err.error?.error || '';

                if (backendError.includes('Invalid email domain')) {
                    this.translate.get('EVENTS.CUSTOM_TOUR.ERRORS.INVALID_EMAIL_DOMAIN').subscribe(res => {
                        this.errorMessage.set(res);
                    });
                } else if (backendError.includes('Missing required fields')) {
                    this.translate.get('EVENTS.CUSTOM_TOUR.ERRORS.MISSING_FIELDS').subscribe(res => {
                        this.errorMessage.set(res);
                    });
                } else if (backendError) {
                    this.errorMessage.set(backendError);
                } else {
                    this.translate.get('EVENTS.CUSTOM_TOUR.ERRORS.GENERIC_ERROR').subscribe(res => {
                        this.errorMessage.set(res || 'Errore durante l\'invio. Riprova.');
                    });
                }
                this.loading.set(false);
            }
        });
    }
}
